import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use environment variable for API URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch products from backend API
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      } else {
        console.error('Failed to fetch products');
        // Fallback to default products if API fails
        setDefaultProducts();
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to default products if API fails
      setDefaultProducts();
    } finally {
      setLoading(false);
    }
  };

  // Set default products as fallback
  const setDefaultProducts = () => {
    const defaultProducts = [
      {
        _id: '1',
        name: 'Homemade Biryani',
        description: 'Traditional rice dish with aromatic spices and tender meat',
        price: 250,
        image: 'https://images.unsplash.com/photo-1563379091339-03246963d8a9?w=400&h=300&fit=crop',
        category: 'Main Course',
        available: true
      },
      {
        _id: '2',
        name: 'Fresh Roti Set',
        description: 'Soft homemade rotis with dal and vegetables',
        price: 120,
        image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=400&h=300&fit=crop',
        category: 'Bread',
        available: true
      },
      {
        _id: '3',
        name: 'Mixed Vegetable Curry',
        description: 'Seasonal vegetables cooked in traditional spices',
        price: 180,
        image: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=400&h=300&fit=crop',
        category: 'Vegetarian',
        available: true
      },
      {
        _id: '4',
        name: 'Chicken Curry',
        description: 'Tender chicken pieces in rich, aromatic gravy',
        price: 280,
        image: 'https://images.unsplash.com/photo-1604335399105-a0c585fd81a2?w=400&h=300&fit=crop',
        category: 'Non-Vegetarian',
        available: true
      },
      {
        _id: '5',
        name: 'Gulab Jamun',
        description: 'Sweet milk dumplings in sugar syrup',
        price: 80,
        image: 'https://images.unsplash.com/photo-1604335399105-a0c585fd81a2?w=400&h=300&fit=crop',
        category: 'Dessert',
        available: true
      },
      {
        _id: '6',
        name: 'Masala Chai',
        description: 'Traditional spiced tea with milk',
        price: 30,
        image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=300&fit=crop',
        category: 'Beverages',
        available: true
      }
    ];
    setProducts(defaultProducts);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const addProduct = async (productData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        const newProduct = await response.json();
        setProducts(prev => [...prev, newProduct]);
        toast.success('Product added successfully!');
        return { success: true };
      } else {
        throw new Error('Failed to add product');
      }
    } catch (error) {
      toast.error('Failed to add product');
      return { success: false };
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      console.log('Updating product with ID:', id, 'Data:', productData);
      const token = localStorage.getItem('token');
      console.log('Token:', token);
      
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });

      console.log('Update response status:', response.status);
      
      if (response.ok) {
        const updatedProduct = await response.json();
        console.log('Updated product:', updatedProduct);
        setProducts(prev => prev.map(product => 
          product._id === id ? updatedProduct : product
        ));
        toast.success('Product updated successfully!');
        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        toast.error(errorData.message || 'Failed to update product');
        return { success: false };
      }
    } catch (error) {
      console.error('Update product error:', error);
      toast.error('Failed to update product');
      return { success: false };
    }
  };

  const deleteProduct = async (id) => {
    try {
      console.log('Deleting product with ID:', id);
      const token = localStorage.getItem('token');
      console.log('Token:', token);
      
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Delete response status:', response.status);
      
      if (response.ok) {
        setProducts(prev => prev.filter(product => product._id !== id));
        toast.success('Product deleted successfully!');
        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('Delete failed:', errorData);
        toast.error(errorData.message || 'Failed to delete product');
        return { success: false };
      }
    } catch (error) {
      console.error('Delete product error:', error);
      toast.error('Failed to delete product');
      return { success: false };
    }
  };

  const toggleProductAvailability = async (id) => {
    try {
      console.log('Toggling availability for product ID:', id);
      const product = products.find(p => p._id === id);
      console.log('Current product:', product);
      const token = localStorage.getItem('token');
      console.log('Token:', token);
      
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ available: !product.available })
      });

      console.log('Toggle response status:', response.status);
      
      if (response.ok) {
        const updatedProduct = await response.json();
        console.log('Updated product:', updatedProduct);
        setProducts(prev => prev.map(product => 
          product._id === id ? updatedProduct : product
        ));
        toast.success(`Product ${product.available ? 'unavailable' : 'available'}!`);
        return { success: true };
      } else {
        const errorData = await response.json();
        console.error('Toggle failed:', errorData);
        toast.error(errorData.message || 'Failed to update product availability');
        return { success: false };
      }
    } catch (error) {
      console.error('Toggle product availability error:', error);
      toast.error('Failed to update product availability');
      return { success: false };
    }
  };

  const getProductById = (id) => {
    return products.find(product => product._id === id);
  };

  const getAvailableProducts = () => {
    return products.filter(product => product.available);
  };

  const value = {
    products,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductAvailability,
    getProductById,
    getAvailableProducts,
    fetchProducts // Expose for manual refresh
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}; 